import React, { createContext, useContext, useEffect } from "react";
import { themeVars } from "./themeVariables";
import {
  UserPreference,
  useUserConfigs,
} from "../shared/customHoooks/useUserConfig";
import { useFeatureFlags } from "../shared/customHoooks/useFeatureFlags";

export type ThemeSetting = UserPreference["theme"];

export type AllThemeName = ThemeSetting | "device-dark" | "device-light";

type ThemeContextType = {
  theme: AllThemeName;
  changeTheme: (newTheme: ThemeSetting) => void;
  isDarkTheme: boolean;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setTheme] = React.useState<AllThemeName>("default");
  const { configs, add } = useUserConfigs<UserPreference>("preference");
  const updateConfig = configs[0]?.updateConfig ?? add;
  const userTheme = configs?.[0]?.data?.config?.theme ?? "default";

  const { features, loading, sync } = useFeatureFlags();
  const flagsReady = sync || !loading;
  const hasThemesFeature =
    flagsReady && (features?.includes("themes") ?? false);
  const effectiveUserTheme: ThemeSetting = !flagsReady
    ? userTheme
    : hasThemesFeature
    ? userTheme
    : "default";

  const detectSystemTheme = (): "dark" | "light" =>
    window.matchMedia?.("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";

  const normalizeTheme = (theme: ThemeSetting): ThemeSetting => {
    if (["light", "dark", "default", "device"].includes(theme)) return theme;
    return "default";
  };

  const applyThemeVariables = (
    theme: ThemeSetting | "device-dark" | "device-light"
  ) => {
    const resolved =
      theme === "device"
        ? detectSystemTheme()
        : theme.startsWith("device-")
        ? (theme.replace("device-", "") as "light" | "dark")
        : theme;

    const allKeys = new Set([
      ...Object.keys(themeVars.light),
      ...Object.keys(themeVars.dark),
    ]);
    allKeys.forEach((key) =>
      document.documentElement.style.removeProperty(key)
    );

    if (resolved !== "default") {
      const vars = themeVars[resolved as "light" | "dark"];
      const defaultVars = themeVars.light;
      const mergedVars = { ...defaultVars, ...vars };

      Object.entries(mergedVars).forEach(([key, value]) => {
        if (value !== undefined) {
          document.documentElement.style.setProperty(key, value);
        }
      });
    }
  };

  useEffect(() => {
    const system = detectSystemTheme();
    const resolvedTheme =
      effectiveUserTheme === "device"
        ? (`device-${system}` as AllThemeName)
        : (effectiveUserTheme as AllThemeName);

    setTheme(resolvedTheme);
    applyThemeVariables(resolvedTheme);

    if (effectiveUserTheme === "device") {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => {
        const newSystem = mq.matches ? "dark" : "light";
        const newDeviceTheme = `device-${newSystem}` as AllThemeName;
        setTheme(newDeviceTheme);
        applyThemeVariables(newDeviceTheme);
      };
      mq.addEventListener("change", handler);
      return () => mq.removeEventListener("change", handler);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveUserTheme]);

  useEffect(() => {
    if (flagsReady && !hasThemesFeature && userTheme !== "default") {
      const currentConfig = configs?.[0]?.data?.config || {};
      updateConfig?.({ ...currentConfig, theme: "default" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsReady, hasThemesFeature, userTheme]);

  const changeTheme = (newTheme: ThemeSetting) => {
    const normalized = normalizeTheme(newTheme);
    const requestedTheme: ThemeSetting = hasThemesFeature
      ? normalized
      : "default";
    const system = detectSystemTheme();
    const effective: AllThemeName =
      requestedTheme === "device"
        ? (`device-${system}` as AllThemeName)
        : (requestedTheme as AllThemeName);

    setTheme(effective);
    applyThemeVariables(effective);

    const currentConfig = configs?.[0]?.data?.config || {};
    updateConfig?.({ ...currentConfig, theme: requestedTheme });
  };

  return (
    <ThemeContext.Provider
      value={{
        theme,
        changeTheme,
        isDarkTheme: theme === "dark" || theme === "device-dark",
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within a ThemeProvider");
  return context;
};
